import { asDate } from "@@/shared/date-time/as-date";
import type { DateRange} from "@@/shared/date-time/date-range";
import { isWithinDateRange } from "@@/shared/date-time/date-range";
import type { Price} from "@@/shared/prices/price";
import { zeroPrice } from "@@/shared/prices/price";
import { subtractFromPrice } from "@@/shared/prices/subtract-from-price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import type { ProviderId } from "@@/shared/providers/provider-id";
import { reportIdFactory } from "@@/shared/reports/report-id";
import type { TicketGiftCard } from "@@/shared/tickets/ticket";

type GiftCardReport = {
    readonly _id: string;
    readonly providerId: ProviderId;
    readonly range: DateRange;

    readonly dates: GiftCardReportDay[];
};

type _helperDates = {
    readonly dates: {
        [date: string]: GiftCardReportDay;
    };
};

type GiftCardReportDay = {
    readonly _id: string;
    readonly dateStr: string;
    readonly date: Date;
    readonly activated: number;
    readonly expired: number;

    readonly activatedAmount: Price;
    readonly expiredAmount: Price;

    readonly activatedTickets: TicketGiftCard[];
    readonly expiredTickets: TicketGiftCard[];
};

const formatReportDateKey = (currentDate: Date) => {
    return `${currentDate.getFullYear()}${(currentDate.getMonth() + 1).toString().padStart(2, "0")}${currentDate.getDate().toString().padStart(2, "0")}`;
};

const emptyReportDay = (dateStr: string, date: Date): GiftCardReportDay => ({
    _id: dateStr,
    dateStr: dateStr,
    date: date,
    activated: 0,
    expired: 0,
    activatedAmount: zeroPrice,
    expiredAmount: zeroPrice,
    activatedTickets: [],
    expiredTickets: [],
});

const giftCardReportFactory = (params: {
    providerId: ProviderId;
    dateRange: DateRange;
    tickets: TicketGiftCard[];
}) => {
    const { providerId, dateRange, tickets } = params;
    const report = tickets.reduce(
        (acc, ticket) => {
            const activatedDate = asDate(ticket.valid.start);
            const expiredDate = asDate(ticket.valid.end);

            const activatedFormattedDateKey =
                formatReportDateKey(activatedDate);
            const expiredFormattedDateKey = formatReportDateKey(expiredDate);

            let activatedDay =
                acc.dates[activatedFormattedDateKey] ??
                emptyReportDay(activatedFormattedDateKey, activatedDate);

            activatedDay = {
                ...activatedDay,
                activated: activatedDay.activated + 1,

                activatedAmount: sumPrices([
                    activatedDay.activatedAmount,
                    ticket.amount,
                ]),

                activatedTickets: [...activatedDay.expiredTickets, ticket],
            };
            let expiredDay =
                acc.dates[expiredFormattedDateKey] ??
                emptyReportDay(expiredFormattedDateKey, expiredDate);

            expiredDay = {
                ...expiredDay,
                expired: expiredDay.expired + 1,

                expiredAmount: sumPrices([
                    expiredDay.expiredAmount,
                    subtractFromPrice(ticket.amount, [ticket.amountUsed]),
                ]),

                expiredTickets: [...expiredDay.expiredTickets, ticket],
            };

            return {
                ...acc,
                dates: {
                    ...acc.dates,
                    [activatedFormattedDateKey]: activatedDay,
                    [expiredFormattedDateKey]: expiredDay,
                },
            };
        },
        {
            dates: {} as Record<string, GiftCardReportDay>,
        } as _helperDates,
    );

    const days = Object.values(report.dates) satisfies GiftCardReportDay[];

    return {
        _id: reportIdFactory(),
        providerId: providerId,
        range: dateRange,
        dates: days.filter(day => isWithinDateRange(day.date, dateRange)),
    } satisfies GiftCardReport;
};

export { giftCardReportFactory };
export type { GiftCardReport, GiftCardReportDay };
